// src/components/site/blocks.tsx — Site officiel Vanessa
// Briques communes aux pages : en-tête de page, sections, cartes, code, appel à l'action.
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, type LucideIcon } from 'lucide-react';
import { buttonClasses, type ButtonVariant } from '@/components/ui/button';

export const Container = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
    <div className={`mx-auto w-full max-w-6xl px-4 ${className}`}>{children}</div>
);

// ── En-tête de page ───────────────────────────────────────────────────────

export const PageHeader = ({ eyebrow, title, intro, children }: {
    eyebrow?: string; title: string; intro?: string; children?: ReactNode;
}) => (
    <div className="border-b border-gray-200 bg-gradient-to-b from-brand-tint to-cream">
        <Container className="py-14 sm:py-20">
            {eyebrow && (
                <p className="mb-3 inline-block rounded-full bg-white px-3 py-1 text-sm font-semibold text-ochre ring-1 ring-brand">
                    {eyebrow}
                </p>
            )}
            <h1 className="max-w-3xl font-display text-4xl font-bold leading-tight text-ink sm:text-5xl">{title}</h1>
            {intro && <p className="mt-4 max-w-2xl text-lg leading-relaxed text-gray-700">{intro}</p>}
            {children && <div className="mt-8 flex flex-wrap gap-3">{children}</div>}
        </Container>
    </div>
);

// ── Section ───────────────────────────────────────────────────────────────

export const Section = ({ id, title, intro, children, tone = 'plain' }: {
    id?: string; title?: string; intro?: string; children: ReactNode; tone?: 'plain' | 'sand';
}) => (
    <section id={id} className={`scroll-mt-20 py-14 sm:py-16 ${tone === 'sand' ? 'bg-sand' : ''}`}>
        <Container>
            {title && <h2 className="font-display text-3xl font-bold text-ink">{title}</h2>}
            {intro && <p className="mt-3 max-w-2xl text-lg text-gray-700">{intro}</p>}
            <div className={title || intro ? 'mt-8' : ''}>{children}</div>
        </Container>
    </section>
);

// ── Cartes ────────────────────────────────────────────────────────────────

export const FeatureCard = ({ icon: Icon, title, children, to, linkLabel }: {
    icon: LucideIcon; title: string; children: ReactNode; to?: string; linkLabel?: string;
}) => (
    <div className="flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-brand-tint text-ochre">
            <Icon className="h-6 w-6" aria-hidden="true" />
        </span>
        <h3 className="font-display text-xl font-semibold text-ink">{title}</h3>
        <div className="mt-2 flex-1 text-base leading-relaxed text-gray-700">{children}</div>
        {to && (
            <Link to={to} className="mt-4 inline-flex items-center gap-1 font-semibold text-ochre hover:underline">
                {linkLabel || 'En savoir plus'} <span aria-hidden="true">→</span>
            </Link>
        )}
    </div>
);

export const Grid = ({ cols = 3, children }: { cols?: 2 | 3 | 4; children: ReactNode }) => (
    <div className={`grid gap-5 ${cols === 2 ? 'md:grid-cols-2' : cols === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
        {children}
    </div>
);

// ── Étapes numérotées ─────────────────────────────────────────────────────

export const Steps = ({ items }: { items: { title: string; text: ReactNode }[] }) => (
    <ol className="space-y-5">
        {items.map((step, i) => (
            <li key={step.title} className="flex gap-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand font-display font-bold text-ink">
                    {i + 1}
                </span>
                <div>
                    <p className="font-display text-lg font-semibold text-ink">{step.title}</p>
                    <div className="mt-0.5 text-base leading-relaxed text-gray-700">{step.text}</div>
                </div>
            </li>
        ))}
    </ol>
);

// ── Bloc de code avec bouton « Copier » ───────────────────────────────────

export const CodeBlock = ({ children, label }: { children: string; label?: string }) => {
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(children);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            // Presse-papier indisponible : la personne peut toujours sélectionner le texte.
        }
    };

    return (
        <div className="my-4 overflow-hidden rounded-2xl bg-ink">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-2">
                <span className="text-sm font-medium text-gray-300">{label || 'Code'}</span>
                <button
                    type="button"
                    onClick={copy}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium text-gray-200 hover:bg-white/10"
                >
                    {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
                    {copied ? 'Copié' : 'Copier'}
                </button>
            </div>
            <pre className="overflow-x-auto p-4 text-sm leading-relaxed text-cream">
                <code>{children}</code>
            </pre>
        </div>
    );
};

export const InlineCode = ({ children }: { children: ReactNode }) => (
    <code className="rounded bg-sand px-1.5 py-0.5 font-mono text-[0.9em] text-ink">{children}</code>
);

// ── Appel à l'action ──────────────────────────────────────────────────────

interface CtaAction {
    label: string;
    to?: string;
    href?: string;
    variant?: ButtonVariant;
}

export const ActionLink = ({ action, size = 'lg' }: { action: CtaAction; size?: 'md' | 'lg' }) => {
    const cls = buttonClasses(action.variant || 'primary', size);
    return action.href ? (
        <a href={action.href} className={cls}>{action.label}</a>
    ) : (
        <Link to={action.to || '/'} className={cls}>{action.label}</Link>
    );
};

const ctaButton = (action: CtaAction, className: string) =>
    action.href ? (
        <a href={action.href} className={className}>{action.label}</a>
    ) : (
        <Link to={action.to || '/'} className={className}>{action.label}</Link>
    );

export const CtaBand = ({ title, text, primary, secondary }: {
    title: string; text?: string; primary: CtaAction; secondary?: CtaAction;
}) => (
    <section className="py-14">
        <Container>
            <div className="rounded-3xl bg-brand px-6 py-12 text-center sm:px-12">
                <h2 className="font-display text-3xl font-bold text-ink">{title}</h2>
                {text && <p className="mx-auto mt-3 max-w-xl text-lg text-ink">{text}</p>}
                <div className="mt-7 flex flex-wrap justify-center gap-3">
                    {ctaButton(
                        primary,
                        'inline-flex min-h-[48px] items-center justify-center rounded-full bg-ink px-6 py-3 font-display font-semibold text-white hover:bg-gray-800'
                    )}
                    {secondary &&
                        ctaButton(
                            secondary,
                            'inline-flex min-h-[48px] items-center justify-center rounded-full border-2 border-ink px-6 py-3 font-display font-semibold text-ink hover:bg-brand-hover'
                        )}
                </div>
            </div>
        </Container>
    </section>
);

// ── Texte courant (pages longues : légal, guide) ──────────────────────────

export const Prose = ({ children }: { children: ReactNode }) => (
    <div className="space-y-4 text-base leading-relaxed text-gray-700 [&_h2]:mt-10 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-ink [&_h3]:mt-6 [&_h3]:font-display [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-ink [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-6 [&_a]:font-medium [&_a]:text-ochre [&_a]:underline">
        {children}
    </div>
);
